import { Children, cloneElement, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAgenciesGetQuery } from '../../Common/Queries/agenciesQueries';
import { useLevelsGetQuery } from '../../Common/Queries/levelQueries';
import { usePanelResourceGetQuery } from '../../Common/Queries/panelQueries';
import { usePositionsGetQuery } from '../../Common/Queries/positionsQueries';
import { useSkillsGetQuery } from '../../Common/Queries/skillQueries';
import { useStreamGetQuery } from '../../Common/Queries/streamQueries';
import { useUsersGetQuery } from '../../Common/Queries/usersQueries';

import { isLogin } from '../../utils';

import { AuthContext, DURATION_TIME, ROLES } from '../../constants';

const paramArray = [
  'positionID',
  'streams',
  'panelID',
  'recruiterID',
  'agencyIDs',
  'skills',
  'levels',
];

export function GetDisplayFilterDataContainer({ children, entity, ...props }) {
  const { userInfo } = useContext(AuthContext);
  const role = userInfo?.roles?.[0];
  const [searchParams] = useSearchParams();

  // Get active params from URL with their values
  const activeParams = useMemo(() => {
    const active = {};
    paramArray.forEach((param) => {
      const value = searchParams.get(param);
      if (value) {
        active[param] = value.split(',').map((v) => v.trim());
      }
    });
    return active;
  }, [searchParams]);

  const isParamActive = (param) => !!activeParams[param]?.length;

  const positionList = usePositionsGetQuery(
    { ids: activeParams.positionID?.join(',') },
    {
      enabled: isLogin() && isParamActive('positionID'),
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const streamList = useStreamGetQuery(
    { ids: activeParams.streams?.join(',') },
    {
      enabled: isLogin() && isParamActive('streams'),
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const recruiterList = useUsersGetQuery(
    {
      ids: activeParams.recruiterID?.join(','),
      role: [
        ROLES.ADMIN,
        ROLES.HR,
        ROLES.HIRING_MANAGER,
        ROLES.SUPER_ADMIN,
        ROLES.TENANT_OWNER,
      ].join(','),
    },
    {
      enabled: isLogin() && role !== ROLES.AGENCY && isParamActive('recruiterID'),
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const panelResourceList = usePanelResourceGetQuery(
    { panelID: activeParams.panelID?.join(',') },
    {
      enabled: isLogin() && entity !== 'Events' && isParamActive('panelID'),
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const eventPanelList = useUsersGetQuery(
    { ids: activeParams.panelID?.join(',') },
    {
      enabled: isLogin() && entity === 'Events' && isParamActive('panelID'),
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const agencyList = useAgenciesGetQuery(
    { ids: activeParams.agencyIDs?.join(',') },
    {
      enabled: isLogin() && role !== ROLES.AGENCY && isParamActive('agencyIDs'),
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const skillList = useSkillsGetQuery(
    { ids: activeParams.skills?.join(',') },
    {
      enabled: isLogin() && isParamActive('skills'),
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const levelList = useLevelsGetQuery(
    { ids: activeParams.levels?.join(',') },
    {
      enabled: isLogin() && isParamActive('levels'),
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  // Check if any active query is loading
  const isLoading =
    (isParamActive('positionID') && positionList.isLoading) ||
    (isParamActive('streams') && streamList.isLoading) ||
    (isParamActive('panelID') &&
      (entity === 'Events' ? eventPanelList.isLoading : panelResourceList.isLoading)) ||
    (isParamActive('recruiterID') && recruiterList.isLoading) ||
    (isParamActive('agencyIDs') && agencyList.isLoading) ||
    (isParamActive('skills') && skillList.isLoading) ||
    (isParamActive('levels') && levelList.isLoading);

  const allData = {
    positionList: positionList?.data?.data || [],
    streamList: streamList?.data?.data || [],
    panelList:
      (entity === 'Events' ? eventPanelList?.data?.data : panelResourceList?.data?.data) || [],
    recruiterList: recruiterList?.data?.data || [],
    agencyList: agencyList?.data?.data || [],
    skillList: skillList?.data?.data || [],
    levelList: levelList?.data?.data || [],
  };

  return Children.only(
    cloneElement(children, {
      allData,
      isLoadingFilterData: isLoading,
      ...props,
    })
  );
}
